/**
 * Precios de modelos — vista BA (REQ-MINT-012 §2; LLD §7).
 *
 * Ruta: `/governance/model-pricing` (panel de Gobernanza). **Panel de precios**: solo precio
 * cliente vigente (calculado en backend), features publicadas y vigencia; sin
 * coste propio, margen ni variación (datos internos).
 *
 * La búsqueda (nombre o proveedor) y la paginación (50/100/200) se resuelven
 * en el backend mediante `GET /models/pricing`, que devuelve únicamente la
 * página solicitada y no calcula benchmarks/telemetría. Sin permiso de precio
 * el backend omite las tarifas y la vista lo declara. Estados vacío/carga/error
 * y card-view en móvil (REQ-UIP-003).
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Coins, Download, RefreshCw } from 'lucide-react';
import { Button, Card, CardContent, SearchInput, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { PageHeader } from '@vanguard-atlas/shared';
import { ProvenanceNote } from '../components/ModelIntelGuard';
import { PricingCatalogTable } from '../components/PricingCatalogTable';
import {
  PRICING_PAGE_SIZES,
  useModelIntelExport,
  useModelIntelPricingList,
  type PricingPageSize,
} from '../hooks/useModelIntel';
import { formatDate } from '../lib/pricing';

const SEARCH_DEBOUNCE_MS = 350;

export default function ModelPricingPage({ showExport = false }: { showExport?: boolean }) {
  const { t } = useI18n();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [provider, setProvider] = useState('');
  const [pageSize, setPageSize] = useState<PricingPageSize>(50);
  const [offset, setOffset] = useState(0);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const exportMutation = useModelIntelExport();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const term = search.trim();
      setDebouncedSearch(term);
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError, refetch, isFetching } = useModelIntelPricingList({
    q: debouncedSearch || undefined,
    provider: provider || undefined,
    limit: pageSize,
    offset,
  });

  const providers = useMemo(() => {
    const values = new Set(data?.providers ?? []);
    if (provider) values.add(provider);
    return [...values].sort();
  }, [data?.providers, provider]);

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;
  const firstItem = total === 0 ? 0 : offset + 1;
  const lastItem = Math.min(offset + pageSize, total);
  const priceHidden = data?.price_visibility === 'omitted';

  const runExport = () => {
    exportMutation.mutate({
      kind: 'models',
      format: exportFormat,
      include_price: !priceHidden,
      provider: provider || null,
      limit: 200,
    });
  };

  return (
    <div className="space-y-4 p-6" data-testid="model-pricing-page">
      <PageHeader
        icon={Coins}
        title={t('modelIntel.pricing.title')}
        subtitle={t('modelIntel.pricing.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="pricing-refresh"
          >
            <RefreshCw className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('modelIntel.pricing.refresh')}
          </Button>
        }
        testId="pricing-header"
      />

      {priceHidden && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
          role="status"
          data-testid="price-hidden-banner"
        >
          {t('modelIntel.pricing.hiddenNotice')}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 text-sm">
        <div className="min-w-[240px] flex-1">
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            placeholder={t('modelIntel.pricing.searchPlaceholder')}
            aria-label={t('modelIntel.pricing.searchPlaceholder')}
            data-testid="pricing-search"
          />
        </div>
        <label className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">
            {t('modelIntel.pricing.filterProvider')}
          </span>
          <select
            aria-label={t('modelIntel.pricing.filterProvider')}
            className="rounded border border-input bg-background px-2 py-1"
            value={provider}
            onChange={(event) => {
              setProvider(event.target.value);
              setOffset(0);
            }}
            data-testid="pricing-filter-provider"
          >
            <option value="">{t('modelIntel.pricing.all')}</option>
            {providers.map((value) => (
              <option key={value} value={value}>
                {providerOptionLabel(t, value)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">
            {t('modelIntel.pricing.pageSize')}
          </span>
          <select
            aria-label={t('modelIntel.pricing.pageSize')}
            className="rounded border border-input bg-background px-2 py-1"
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value) as PricingPageSize);
              setOffset(0);
            }}
            data-testid="pricing-page-size"
          >
            {PRICING_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? (
        <div
          className="flex items-center gap-2 py-8 text-sm text-gray-500"
          data-testid="pricing-loading"
        >
          <Spinner size="sm" />
        </div>
      ) : isError ? (
        <div
          className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
          role="alert"
          data-testid="pricing-error"
        >
          <p>{t('modelIntel.pricing.error')}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
            {t('modelIntel.pricing.retry')}
          </Button>
        </div>
      ) : priceHidden ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {t('modelIntel.pricing.hiddenNotice')}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <PricingCatalogTable
              items={data?.items ?? []}
              emptyLabel={t('modelIntel.pricing.empty')}
            />
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="pricing-range">
          {t('modelIntel.pricing.range', {
            first: firstItem,
            last: lastItem,
            total,
          })}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((value) => Math.max(0, value - pageSize))}
            disabled={currentPage <= 1 || isFetching}
            data-testid="pricing-prev"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            {t('modelIntel.pricing.previous')}
          </Button>
          <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="pricing-page">
            {t('modelIntel.pricing.pageOf', { page: currentPage, pages: pageCount })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((value) => value + pageSize)}
            disabled={currentPage >= pageCount || isFetching}
            data-testid="pricing-next"
          >
            {t('modelIntel.pricing.next')}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {showExport && (
        <Card data-testid="governance-export">
          <CardContent className="pt-6">
            <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold">
              <Download className="h-5 w-5 text-primary" aria-hidden="true" />
              {t('modelIntel.governance.exportTitle')}
            </h2>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('modelIntel.governance.exportHint')}
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  {t('modelIntel.governance.exportFormat')}
                </span>
                <select
                  className="mt-1 rounded border border-input bg-background px-2 py-1 text-sm"
                  value={exportFormat}
                  onChange={(event) => setExportFormat(event.target.value as 'csv' | 'json')}
                  data-testid="export-format"
                >
                  <option value="csv">{t('modelIntel.governance.formatCsv')}</option>
                  <option value="json">{t('modelIntel.governance.formatJson')}</option>
                </select>
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={runExport}
                disabled={exportMutation.isPending}
                data-testid="export-run"
              >
                {t('modelIntel.governance.exportAction')}
              </Button>
            </div>
            {exportMutation.isError && (
              <p className="mt-3 text-sm text-red-600" role="alert" data-testid="export-error">
                {t('modelIntel.governance.exportError')}
              </p>
            )}
            {exportMutation.data && (
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-300" data-testid="export-done">
                {t('modelIntel.governance.exportDone', {
                  rows: exportMutation.data.row_count,
                })}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        {data && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('modelIntel.pricing.contract', {
              version: data.contract_version,
              date: formatDate(data.generated_at),
            })}
          </p>
        )}
        <ProvenanceNote />
      </div>
    </div>
  );
}

function providerOptionLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  provider: string,
): string {
  const map: Record<string, string> = {
    azure_foundry: 'azureFoundry',
    aws_bedrock: 'awsBedrock',
    vertex_ai: 'vertexAi',
    voyage: 'voyage',
    anthropic: 'anthropic',
  };
  const key = map[provider];
  return key ? t(`modelIntel.providers.${key}`) : provider;
}
